import { createRouter, createWebHistory } from '@ionic/vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { hasAccessToken } from '@/services/api/erpat-client';
import { parkRouteForSignIn } from '@/services/realtime-navigation';

const ONBOARDING_KEY = 'syntry.onboarding_seen';

/** Routes reachable without an authenticated session. */
const PUBLIC_ROUTE_NAMES = new Set(['onboarding', 'sign-in']);

/** Whether a route name belongs to the public (unauthenticated) surface. */
export const isPublicRouteName = (name: unknown): boolean =>
  typeof name === 'string' && PUBLIC_ROUTE_NAMES.has(name);

export const hasSeenOnboarding = (): boolean => {
  try {
    // Check if the value is a valid date string (for backward compatibility), but any value presence indicates onboarding was seen.
    return localStorage.getItem(ONBOARDING_KEY) !== null && !isNaN(Date.parse(localStorage.getItem(ONBOARDING_KEY)!));
  } catch {
    return false;
  }
};

export const markOnboardingSeen = (): void => {
  try {
    //Set to current YYYY-MM-DD date string for better debugging, but the value itself isn't important for the check.
    localStorage.setItem(ONBOARDING_KEY, new Date().toISOString().split('T')[0]!);
  } catch {
    /* no-op (private mode etc.) */
  }
};

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => (hasSeenOnboarding() ? '/auth/sign-in' : '/onboarding'),
  },
  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('@/views/onboarding/OnboardingPage.vue'),
  },
  {
    path: '/auth/sign-in',
    name: 'sign-in',
    component: () => import('@/views/auth/SignInPage.vue'),
  },
  {
    path: '/tabs',
    component: () => import('@/components/layout/TabsLayout.vue'),
    children: [
      {
        path: '',
        redirect: '/tabs/home',
      },
      {
        path: 'home',
        name: 'home',
        component: () => import('@/views/home/HomePage.vue'),
      },
      {
        path: 'activity',
        name: 'activity',
        component: () => import('@/views/timesheets/TimesheetPage.vue'),
      },
      {
        path: 'assistant',
        name: 'assistant',
        component: () => import('@/views/assistant/AIAssistantPage.vue'),
      },
      {
        path: 'requests',
        name: 'requests',
        component: () => import('@/views/requests/RequestsPage.vue'),
      },
      {
        path: 'inbox',
        name: 'inbox',
        component: () => import('@/views/inbox/InboxPage.vue'),
      },
      {
        path: 'inbox/:id',
        redirect: (to) => `/tabs/messages/${to.params.id}`,
      },
      {
        path: 'messages',
        name: 'messages',
        component: () => import('@/views/messages/MessagesPage.vue'),
      },
      {
        path: 'messages/:id',
        name: 'conversation',
        // Group chats have their own page. `mapConversation` namespaces group
        // ids with a `g-` prefix; redirect those (and any legacy `g-` deep link)
        // to the dedicated group route so direct threads keep this component.
        beforeEnter: (to) => {
          const raw = to.params.id;
          const id = Array.isArray(raw) ? raw[0] : raw;
          if (typeof id === 'string' && id.startsWith('g-')) {
            return { name: 'group-conversation', params: { id: id.slice(2) } };
          }
          return true;
        },
        component: () => import('@/views/messages/ConversationPage.vue'),
      },
      {
        path: 'messages/group/:id',
        name: 'group-conversation',
        component: () => import('@/views/messages/GroupConversationPage.vue'),
      },
      {
        path: 'messages/group/:id/members',
        name: 'group-members',
        component: () => import('@/views/messages/GroupMembersPage.vue'),
      },
      {
        // Profile is now a full-screen sub-page (top-level `/profile-settings`);
        // keep this path working for any deep link / cached reference.
        path: 'profile',
        redirect: '/profile-settings',
      },
      {
        path: 'modules',
        name: 'modules',
        component: () => import('@/views/modules/ModulesPage.vue'),
      },
    ],
  },
  {
    path: '/profile',
    name: 'user-profile',
    component: () => import('@/views/profile/UserProfilePage.vue'),
  },
  {
    // Same page, viewing another member (Message enabled, posts scoped to them).
    path: '/profile/:userId',
    name: 'member-profile',
    component: () => import('@/views/profile/UserProfilePage.vue'),
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/about/AboutPage.vue'),
  },
  {
    path: '/support',
    name: 'support',
    component: () => import('@/views/support/SupportPage.vue'),
  },
  {
    path: '/help',
    name: 'help',
    component: () => import('@/views/help/HelpCenterPage.vue'),
  },
  {
    path: '/website',
    name: 'website',
    component: () => import('@/views/website/WebsitePage.vue'),
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/privacy/PrivacyPolicyPage.vue'),
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/terms/TermsOfServicePage.vue'),
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/settings/ResetPasswordPage.vue'),
  },
  {
    path: '/assistant/history',
    name: 'assistant-history',
    component: () => import('@/views/assistant/AIChatHistoryPage.vue'),
  },
  {
    path: '/notifications',
    redirect: '/tabs/inbox',
  },
  {
    path: '/attendance',
    redirect: '/tabs/activity',
  },
  {
    path: '/attendance/list',
    name: 'attendance-list',
    component: () => import('@/views/attendance/AttendanceListPage.vue'),
  },
  {
    path: '/leave',
    redirect: '/tabs/activity',
  },
  {
    // Profile Settings — a full-screen sub-page (SubPageHeader, no tab bar).
    path: '/profile-settings',
    name: 'profile',
    component: () => import('@/views/profile/ProfilePage.vue'),
  },
  {
    path: '/settings',
    redirect: '/profile-settings',
  },
  {
    // The first realtime client declared a second page at this path. It was
    // folded into `/notification-settings` (one page, one route name); keep
    // the old path reachable for anything that bookmarked it.
    path: '/settings/notifications',
    redirect: '/notification-settings',
  },
  {
    // Notification preferences — the event-reminder lead (a per-user GLOBAL
    // server setting, not per event), device notifications, and the
    // live-updates connection status + test button. A full-screen sub-page
    // beside Profile Settings rather than a section inside it: the reminder
    // lead is also reachable straight from an event, and a deep link into a
    // scrolled sub-section of Profile has no precedent here.
    path: '/notification-settings',
    name: 'notification-settings',
    component: () => import('@/views/settings/NotificationSettingsPage.vue'),
  },
  {
    // Security PIN — set, change or remove the 4-digit PIN that unlocks the
    // confidential screens (pay, salary records, government IDs) instead of the
    // password, and unlock a PIN that locked after too many wrong tries.
    // Authenticated like every route not named in PUBLIC_ROUTE_NAMES; reached
    // from the account drawer's Personal Preference list.
    path: '/security-pin',
    name: 'security-pin',
    component: () => import('@/views/settings/SecurityPinPage.vue'),
  },
  {
    // Sync — the punches saved on THIS device by offline attendance: whether
    // the device can save punches offline, the sync state ("Sync now"), and
    // every punch still waiting or refused. Authenticated like every route not
    // named in PUBLIC_ROUTE_NAMES; reached from the account drawer's Sync row
    // and the Timesheet's sync badge.
    path: '/sync-issues',
    name: 'sync-issues',
    component: () => import('@/views/settings/SyncIssuesPage.vue'),
  },
  {
    path: '/notes',
    name: 'notes',
    component: () => import('@/views/notes/NotesPage.vue'),
  },
  {
    path: '/todo',
    name: 'todo',
    component: () => import('@/views/todo/TodoPage.vue'),
  },
  {
    path: '/mood',
    name: 'mood',
    component: () => import('@/views/mood/MoodPage.vue'),
  },
  {
    // The caller's own activity trail (their ERPat system logs). NOT the
    // `activity` TAB route — that one is the Timesheet (/tabs/activity).
    path: '/activity',
    name: 'activity-log',
    component: () => import('@/views/activity/ActivityPage.vue'),
  },
  {
    // The Reports module. Which report is showing rides on `?report=<id>` so a
    // deep link (the Home absences widget) survives more reports being added.
    path: '/reports',
    name: 'reports',
    component: () => import('@/views/reports/ReportsPage.vue'),
  },
  {
    path: '/leaves',
    name: 'leaves',
    component: () => import('@/views/leaves/LeavesPage.vue'),
  },
  {
    // The team review queue is a MODE of the Leaves page, not a separate page —
    // one implementation behind the "My Leaves / For Review" switcher. This path
    // stays as a stable deep link (the Modules tile and the Home widget point at
    // the query form) and simply opens that tab. Deliberately NOT guarded:
    // authorization is the server's, and a caller without access lands on the
    // panel's own "not available" state.
    path: '/leaves/approvals',
    redirect: { path: '/leaves', query: { view: 'review' } },
  },
  {
    path: '/payslips',
    name: 'payslips',
    component: () => import('@/views/payslips/PayslipsPage.vue'),
  },
  {
    path: '/payslips/preview/:id?',
    name: 'payslip-preview',
    component: () => import('@/views/payslips/PayslipPreviewPage.vue'),
  },
  {
    path: '/loans',
    name: 'loans',
    component: () => import('@/views/loans/LoansPage.vue'),
  },
  {
    // Which section is showing rides on `?tab=overview|schedule|payments`, so a
    // tab survives a reload and can be linked to. Declared after `/loans` so the
    // static path is never swallowed by the dynamic segment.
    path: '/loans/:id',
    name: 'loan-detail',
    component: () => import('@/views/loans/LoanDetailPage.vue'),
  },
  {
    // The PIN-or-password gate lives in the page, not here: it must re-prompt on
    // every entry, and a guard-level prompt has no precedent in this router.
    path: '/salary-records',
    name: 'salary-records',
    component: () => import('@/views/salary/SalaryRecordsPage.vue'),
  },
  {
    path: '/announcements',
    name: 'announcements',
    component: () => import('@/views/announcements/AnnouncementsPage.vue'),
  },
  {
    path: '/announcements/:id',
    name: 'advisory-detail',
    component: () => import('@/views/announcements/AdvisoryDetailPage.vue'),
  },
  {
    path: '/departments',
    name: 'departments',
    component: () => import('@/views/departments/DepartmentsPage.vue'),
  },
  {
    path: '/people',
    name: 'people',
    component: () => import('@/views/people/PeoplePage.vue'),
  },
  {
    // The employee Calendar. View, month, selected day and the active source
    // filters all ride on the query (`?view=&m=YYYY-MM&d=YYYY-MM-DD&src=`), so a
    // deep link opens exactly one period and Back behaves predictably.
    //
    // `/holidays` and `/events` stay where they are. Redirecting them into
    // Calendar is only safe once it is at parity with both, and it is not: the
    // Events pages own RSVP, the agenda, attachments and the attendee roster,
    // none of which Calendar renders. Events now runs on live data
    // (`/v1/api/me/events`), so the old "nothing to redirect INTO yet" caveat is
    // gone — parity is the remaining blocker, not data.
    path: '/calendar',
    name: 'calendar',
    component: () => import('@/views/calendar/CalendarPage.vue'),
  },
  {
    path: '/holidays',
    name: 'holidays',
    component: () => import('@/views/holidays/HolidaysPage.vue'),
  },
  {
    path: '/schedules',
    name: 'schedules',
    component: () => import('@/views/schedules/SchedulesPage.vue'),
  },
  // Events module. `/events/:id/attendees` is declared before `/events/:id` for
  // READABILITY, matching how `/projects/tasks`, `/ticketing/all` and the
  // `/my-assets` routes are laid out — not because the order is load-bearing.
  // vue-router 4 resolves by a RANKED matcher (more segments, and static
  // segments over dynamic ones, score higher), and declaration order is only the
  // tie-break between routes of EQUAL rank. This pair never reaches that
  // tie-break: `/events/:id` is a single dynamic segment and cannot match the
  // two-segment `/events/12/attendees` at any position. Do not copy this block
  // as evidence that "a sub-path left after a bare `:id` gets swallowed".
  {
    path: '/events',
    name: 'events',
    component: () => import('@/views/events/EventsPage.vue'),
  },
  {
    path: '/events/:id/attendees',
    name: 'event-attendees',
    component: () => import('@/views/events/EventAttendeesPage.vue'),
  },
  {
    // A recurring series returns several occurrences under ONE id, so which one
    // the detail page is showing rides on `?d=YYYY-MM-DD` (optional — absent
    // means the base/first occurrence). Keeping it in the query rather than the
    // path means a non-recurring deep link stays `/events/12`.
    path: '/events/:id',
    name: 'event-detail',
    component: () => import('@/views/events/EventDetailPage.vue'),
  },
  {
    // Employee-facing Movements / Internal Job Portal concept. This remains a
    // clearly labelled sample-data preview until the end-user API is wired.
    path: '/internal-mobility',
    name: 'internal-mobility',
    component: () => import('@/views/movements/InternalMobilityPage.vue'),
  },
  {
    path: '/disciplinary',
    name: 'disciplinary',
    component: () => import('@/views/disciplinary/DisciplinaryPage.vue'),
  },
  {
    // Compliance: the investigations the signed-in employee LEADS (ERPat Compliance 1.6.0).
    path: '/investigations',
    name: 'investigations',
    component: () => import('@/views/investigations/InvestigationsPage.vue'),
  },
  {
    path: '/investigations/:id',
    name: 'investigation-detail',
    component: () => import('@/views/investigations/InvestigationDetailPage.vue'),
  },
  {
    // Compliance: the corrective actions the signed-in employee OWNS or that are ABOUT them.
    path: '/corrective-actions',
    name: 'corrective-actions',
    component: () => import('@/views/corrective-actions/CorrectiveActionsPage.vue'),
  },
  {
    path: '/corrective-actions/:id',
    name: 'corrective-action-detail',
    component: () => import('@/views/corrective-actions/CorrectiveActionDetailPage.vue'),
  },
  // Project Management module. Order matters twice over:
  //  - `/projects/tasks`, `/projects/tasks/:id` and `/projects/milestones` are
  //    declared BEFORE `/projects/:id`, or those static segments would be
  //    swallowed as a project id;
  //  - `/projects` is safe from the `firebase.json` trap that forced Assets onto
  //    `/my-assets`: only `/assets/*` carries the immutable 1-year Cache-Control
  //    that would serve a year-stale app shell for an app route.
  {
    path: '/projects',
    name: 'projects',
    component: () => import('@/views/projects/ProjectsPage.vue'),
  },
  {
    // "My Tasks" — the cross-project task list, and the module's primary surface.
    path: '/projects/tasks',
    name: 'project-my-tasks',
    component: () => import('@/views/projects/MyTasksPage.vue'),
  },
  {
    path: '/projects/tasks/:id',
    name: 'project-task-detail',
    component: () => import('@/views/projects/ProjectTaskDetailPage.vue'),
  },
  {
    // Milestones across every project — list or Gantt, chosen with `?view=`.
    path: '/projects/milestones',
    name: 'project-milestones',
    component: () => import('@/views/projects/MilestonesPage.vue'),
  },
  {
    // Timesheets — the employee's own completed project time.
    path: '/projects/timesheets',
    name: 'project-timesheets',
    component: () => import('@/views/projects/TimesheetsPage.vue'),
  },
  {
    path: '/projects/:id',
    name: 'project-detail',
    component: () => import('@/views/projects/ProjectDetailPage.vue'),
  },
  // Ticketing module. Static sub-paths (`all`, `new`) are declared before the
  // `:id` detail route so they never get swallowed by the dynamic segment.
  {
    path: '/ticketing',
    name: 'ticketing',
    component: () => import('@/views/ticketing/TicketingDashboardPage.vue'),
  },
  {
    path: '/ticketing/all',
    name: 'ticketing-all',
    component: () => import('@/views/ticketing/TicketingListPage.vue'),
  },
  {
    path: '/ticketing/new',
    name: 'ticketing-new',
    component: () => import('@/views/ticketing/TicketingNewPage.vue'),
  },
  {
    path: '/ticketing/:id',
    name: 'ticketing-detail',
    component: () => import('@/views/ticketing/TicketingDetailPage.vue'),
  },
  // Forms module. The list page is declared before the `:uuid` answering route
  // so `/forms` never gets swallowed by the dynamic segment.
  {
    path: '/forms',
    name: 'forms',
    component: () => import('@/views/forms/FormsListPage.vue'),
  },
  {
    path: '/forms/:uuid',
    name: 'form-answer',
    component: () => import('@/views/forms/FormPage.vue'),
  },
  {
    path: '/knowledge-base',
    name: 'knowledge-base',
    component: () => import('@/views/knowledge-base/KnowledgeBasePage.vue'),
  },
  {
    path: '/knowledge-base/:id',
    name: 'knowledge-base-article',
    component: () => import('@/views/knowledge-base/KnowledgeBaseArticlePage.vue'),
  },
  // Employee Handbook — the company handbook from ERPat's Helpdesk module
  // (`/v1/api/me/handbook/*`): server-owned chapters over published sections.
  // Distinct from Knowledge Base above, which is the org-wide Help Center.
  //
  // Every sub-path is explicitly prefixed (`sections` / `category` / `article`)
  // rather than hanging a bare `:id` off `/handbook`, so no dynamic segment can
  // ever swallow a sibling. `sections` is listed first regardless, per the
  // static-before-dynamic convention used by the `/my-assets` routes below.
  {
    path: '/handbook',
    name: 'handbook',
    component: () => import('@/views/handbook/HandbookPage.vue'),
  },
  {
    path: '/handbook/sections',
    name: 'handbook-sections',
    component: () => import('@/views/handbook/HandbookSectionsPage.vue'),
  },
  {
    // Same component as `handbook-sections` — a chapter is just that list with a
    // `category_id` filter.
    path: '/handbook/category/:id',
    name: 'handbook-category',
    component: () => import('@/views/handbook/HandbookSectionsPage.vue'),
  },
  {
    path: '/handbook/article/:id',
    name: 'handbook-article',
    component: () => import('@/views/handbook/HandbookArticlePage.vue'),
  },
  // Assets (Safekeep) module — the employee's own endorsed equipment, live against
  // ERPat's `modules/Assets/` end-user API. Static sub-paths (`endorsements`,
  // `caretaker`) are declared before the `:id` detail route so they are never
  // swallowed by the dynamic segment.
  //
  // ⚠ The prefix is `/my-assets`, NOT `/assets`. `/assets` is the hosting's
  // build-output namespace: firebase.json stamps `Cache-Control: immutable,
  // max-age=31536000` on `/assets/*` (and a day on `/assets/**`), and Firebase
  // applies headers by REQUEST path — including paths served through the
  // catch-all `** -> /index.html` rewrite. An app route under `/assets` would
  // therefore hand out an index.html cached for a year on any deep link, hard
  // refresh or PWA cold start, defeating the update machinery in
  // `src/pwa/version-check.ts`. The service worker and the Vite dev server both
  // mask it, so it would pass local testing and break only in production.
  {
    path: '/my-assets',
    name: 'my-assets',
    component: () => import('@/views/assets/AssetsListPage.vue'),
  },
  {
    path: '/my-assets/endorsements',
    name: 'asset-endorsements',
    component: () => import('@/views/assets/AssetEndorsementsPage.vue'),
  },
  {
    path: '/my-assets/endorsements/:id',
    name: 'asset-endorsement-detail',
    component: () => import('@/views/assets/AssetEndorsementDetailPage.vue'),
  },
  {
    path: '/my-assets/caretaker',
    name: 'caretaker-assets',
    component: () => import('@/views/assets/CaretakerAssetsPage.vue'),
  },
  {
    path: '/my-assets/:id',
    name: 'asset-detail',
    component: () => import('@/views/assets/AssetDetailPage.vue'),
  },
  {
    path: '/my-assets/:id/report',
    name: 'asset-report-issue',
    component: () => import('@/views/assets/AssetReportIssuePage.vue'),
  },
  {
    // The module shipped as an "In Preview" mock at this path; keep cached deep
    // links and any bookmark working.
    path: '/preview/assets',
    redirect: '/my-assets',
  },
  {
    // Communication module — company Timeline feed (real data).
    path: '/timeline',
    name: 'timeline',
    component: () => import('@/views/timeline/TimelinePage.vue'),
  },
  {
    // Government Access — branded launcher pages for the PH member portals
    // (SSS / Pag-IBIG / PhilHealth / BIR). Content: src/utils/gov-portals.ts.
    // Unknown slugs bounce back to Modules from inside the page.
    path: '/government/:slug',
    name: 'government-portal',
    component: () => import('@/views/government/GovernmentPortalPage.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

/* Guards:
   - Onboarding gate: returning user skips onboarding; first-timer is sent to it.
   - Auth gate: protected routes require a cached access token; an authenticated
     user hitting sign-in is bounced to the app. The route it bounced off is
     PARKED (tenant-stamped, user unknown) so the sign-in success sites can
     land the user where they were headed — a notification tap or deep link
     that arrived before sign-in survives the round trip (phase6-contract §3.7).
*/
router.beforeEach((to) => {
  const authed = hasAccessToken();
  const isPublic = typeof to.name === 'string' && PUBLIC_ROUTE_NAMES.has(to.name);

  // Onboarding routing first (independent of auth).
  if (to.name === 'onboarding' && hasSeenOnboarding()) {
    return authed ? { name: 'home' } : { name: 'sign-in' };
  }
  if (to.name === 'sign-in' && !hasSeenOnboarding()) {
    return { name: 'onboarding' };
  }

  // Already signed in → no reason to see the sign-in page.
  if (to.name === 'sign-in' && authed) {
    return { name: 'home' };
  }

  // Protected route without a session → park it, then send to sign-in.
  if (!isPublic && !authed) {
    parkRouteForSignIn(to.fullPath);
    return { name: 'sign-in' };
  }

  return true;
});

export default router;
